<template>
    <div class="p-4">
        <!-- Filtros plegables -->
        <div class="card mb-3">
            <div
                class="card-header d-flex align-items-center justify-content-between py-2"
                style="cursor: pointer;"
                @click="filtrosAbiertos = !filtrosAbiertos"
            >
                <span class="fw-semibold small">
                    <i class="bi bi-funnel me-1" />
                    Filtros
                </span>
                <i :class="filtrosAbiertos ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi small text-muted" />
            </div>

            <div v-show="filtrosAbiertos" class="card-body py-2">
                <div class="row g-2 align-items-end">
                    <div class="col-12 col-md-4">
                        <label class="form-label small mb-1">Buscar</label>
                        <input
                            v-model="search"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Descripción, cliente..."
                            @input="onSearchInput"
                        />
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label small mb-1">Avisos</label>
                        <select v-model="todos" class="form-select form-select-sm" @change="actualizarUrl(1)">
                            <option :value="false">Mis avisos</option>
                            <option :value="true">Todos</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label small mb-1">Estado</label>
                        <select v-model="finalizado" class="form-select form-select-sm" @change="actualizarUrl(1)">
                            <option value="">Abierto</option>
                            <option value="1">Cerrado</option>
                            <option value="todos">Todos</option>
                        </select>
                    </div>
                    <div class="col-auto">
                        <button class="btn btn-sm btn-secondary" @click="limpiarFiltros">
                            Limpiar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabla -->
        <div class="card">
            <div class="card-body p-0">
                <!-- Loading -->
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-secondary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="alert alert-danger m-3">
                    {{ error }}
                </div>

                <!-- Empty -->
                <div v-else-if="resultado && resultado.items.length === 0" class="text-center text-muted py-5 small">
                    No se encontraron avisos.
                </div>

                <!-- Datos -->
                <template v-else-if="resultado">
                    <!-- Cards (mobile) -->
                    <div class="d-md-none overflow-hidden">
                        <RouterLink
                            v-for="aviso in resultado.items"
                            :key="aviso.idAviso"
                            class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                            :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }"
                        >
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <span class="text-muted small">#{{ aviso.idAviso }} · {{ formatFecha(aviso.fecha) }}</span>
                                <span class="badge" :class="aviso.finalizado ? 'bg-success' : 'bg-warning text-dark'">
                                    {{ aviso.finalizado ? 'Cerrado' : 'Abierto' }}
                                </span>
                            </div>
                            <div class="fw-semibold small" v-html="highlight(aviso.cliente?.nombreFiscal, search)" />
                            <div class="small text-clamp-2" v-html="highlight(aviso.descripcion, search)" />
                            <div v-if="aviso.asignacionesTexto" class="small text-muted mt-1" v-html="highlight(aviso.asignacionesTexto, search)" />
                        </RouterLink>
                    </div>

                    <!-- Tabla (desktop) -->
                    <div class="d-none d-md-block table-responsive">
                        <table class="table table-hover table-sm mb-0">
                            <thead class="table-light">
                                <tr>
                                    <th class="ps-3 border-0 small">#</th>
                                    <th class="border-0 small">Fecha</th>
                                    <th class="border-0 small">Cliente</th>
                                    <th class="border-0 small">Descripción</th>
                                    <th class="d-none d-lg-table-cell border-0 small">Asignaciones</th>
                                    <th class="border-0 small">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="aviso in resultado.items"
                                    :key="aviso.idAviso"
                                >
                                    <td class="ps-0 small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-muted ps-3 py-2 d-block stretched-link">
                                            {{ aviso.idAviso }}
                                        </RouterLink>
                                    </td>
                                    <td class="small text-nowrap border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-dark py-2 d-block stretched-link">
                                            {{ formatFecha(aviso.fecha) }}
                                        </RouterLink>
                                    </td>
                                    <td class="small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(aviso.cliente?.nombreFiscal, search)" />
                                    </td>
                                    <td class="small border-0 py-0 position-relative" style="max-width: 280px;">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-dark py-2 d-block stretched-link">
                                            <span class="text-truncate d-block" v-html="highlight(aviso.descripcion, search)" />
                                        </RouterLink>
                                    </td>
                                    <td class="small d-none d-lg-table-cell text-muted border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-muted py-2 d-block stretched-link" v-html="highlight(aviso.asignacionesTexto, search)" />
                                    </td>
                                    <td class="border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none py-2 d-block stretched-link">
                                            <span
                                                class="badge"
                                                :class="aviso.finalizado ? 'bg-success' : 'bg-warning text-dark'"
                                            >
                                                {{ aviso.finalizado ? 'Cerrado' : 'Abierto' }}
                                            </span>
                                        </RouterLink>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Paginación -->
                    <div class="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                        <span class="small text-muted">
                            {{ resultado.totalCount }} registros &middot; Página {{ resultado.page }} de {{ resultado.totalPages }}
                        </span>
                        <div class="d-flex gap-1">
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page <= 1"
                                @click="cambiarPagina(resultado.page - 1)"
                            >
                                <i class="bi bi-chevron-left" />
                            </button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page >= resultado.totalPages"
                                @click="cambiarPagina(resultado.page + 1)"
                            >
                                <i class="bi bi-chevron-right" />
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { misAvisosApi } from '@/api/misAvisosApi'
import type { Aviso, PaginatedResponse } from '@/types/aviso'
import { highlight } from '@/utils/highlight'

usePageHeader('Mis avisos')
useBreadcrumbs([{ label: 'Mis avisos' }])

const PAGE_SIZE = 20

const route = useRoute()
const router = useRouter()

const filtrosAbiertos = ref(window.innerWidth >= 768)
const todos = ref((route.query.todos as string) === '1')
const search = ref((route.query.search as string) ?? '')
const finalizado = ref((route.query.finalizado as string) ?? '')
const loading = ref(false)
const error = ref<string | null>(null)
const resultado = ref<PaginatedResponse<Aviso> | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function onSearchInput() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => actualizarUrl(1), 400)
}

function limpiarFiltros() {
    search.value = ''
    finalizado.value = ''
    todos.value = false
    actualizarUrl(1)
}

function cambiarPagina(pagina: number) {
    actualizarUrl(pagina)
}

function actualizarUrl(pagina: number) {
    router.push({
        query: {
            ...(todos.value ? { todos: '1' } : {}),
            ...(search.value ? { search: search.value } : {}),
            ...(finalizado.value ? { finalizado: finalizado.value } : {}),
            ...(pagina > 1 ? { page: String(pagina) } : {}),
        },
    })
}

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

async function cargar() {
    const pagina = Number(route.query.page) || 1
    const finalizadoParam = finalizado.value === 'todos' ? undefined : finalizado.value === '1' ? 1 : 0

    loading.value = true
    error.value = null
    try {
        resultado.value = await misAvisosApi.getAll({
            ...(todos.value ? { todos: 1 } : {}),
            search: search.value || undefined,
            finalizado: finalizadoParam,
            page: pagina,
            pageSize: PAGE_SIZE,
        })
    } catch {
        error.value = 'No se pudieron cargar los avisos.'
    } finally {
        loading.value = false
    }
}

watch(
    () => route.query,
    (q) => {
        todos.value = q.todos === '1'
        search.value = (q.search as string) ?? ''
        finalizado.value = (q.finalizado as string) ?? ''
        cargar()
    },
    { immediate: true },
)
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
